import { Search, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { FmpInsiderTradingFileResponse } from '@/services/api';
import { searchInsiderTrades, type InsiderTradeSearchResult } from '@/utils/insiderTradingSearch';
import type { InsiderTransactionCode } from '@/utils/insiderTradingTransactionTypes';

export function InsiderTradingLayerSearch({
  layerActive,
  payload,
  enabledCodes,
  chartDates,
  searchQuery,
  onSearchQueryChange,
  onSelectResult,
}: {
  layerActive: boolean;
  payload: FmpInsiderTradingFileResponse | null;
  enabledCodes: ReadonlySet<InsiderTransactionCode>;
  chartDates: ReadonlySet<string>;
  searchQuery: string;
  onSearchQueryChange: (query: string) => void;
  onSelectResult: (result: InsiderTradeSearchResult) => void;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    const t = window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => window.clearTimeout(t);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      if (rootRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const results = useMemo(
    () => searchInsiderTrades(payload, searchQuery, enabledCodes, chartDates),
    [payload, searchQuery, enabledCodes, chartDates]
  );

  const grouped = useMemo(() => {
    const map = new Map<string, InsiderTradeSearchResult[]>();
    for (const r of results) {
      const list = map.get(r.transactionDate) ?? [];
      list.push(r);
      map.set(r.transactionDate, list);
    }
    return [...map.entries()];
  }, [results]);

  if (!layerActive) return null;

  return (
    <div ref={rootRef} className="relative shrink-0">
      <button
        type="button"
        aria-label="Search insider trades"
        aria-expanded={open}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        className={`flex h-6 w-6 items-center justify-center rounded-md transition-colors focus:outline-none focus-visible:ring-1 focus-visible:ring-amber-400 ${
          open || searchQuery.trim()
            ? 'bg-amber-50 text-amber-800'
            : 'text-amber-700 hover:bg-amber-50'
        }`}
      >
        <Search className="h-3.5 w-3.5" />
      </button>

      {open ? (
        <div
          role="dialog"
          aria-label="Search insider trades by insider name"
          className="absolute left-0 top-full z-50 mt-1.5 w-72 rounded-lg border border-slate-200 bg-white shadow-lg"
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="border-b border-slate-100 p-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
              <input
                ref={inputRef}
                type="search"
                value={searchQuery}
                onChange={(e) => onSearchQueryChange(e.target.value)}
                placeholder="Insider name, role, or CIK…"
                className="w-full rounded-md border border-slate-200 py-1.5 pl-7 pr-7 text-xs text-slate-800 placeholder:text-slate-400 focus:border-amber-400 focus:outline-none focus:ring-1 focus:ring-amber-400"
              />
              {searchQuery ? (
                <button
                  type="button"
                  aria-label="Clear search"
                  onClick={() => onSearchQueryChange('')}
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              ) : null}
            </div>
            <p className="mt-1.5 px-0.5 text-[10px] text-slate-500">
              Filters chart markers while typing · respects type filter
            </p>
          </div>

          <div className="institutional-holders-ribbon-scroll max-h-64 overflow-y-auto py-1">
            {searchQuery.trim().length < 2 ? (
              <p className="px-3 py-4 text-center text-xs text-slate-400">
                Type at least 2 characters
              </p>
            ) : results.length === 0 ? (
              <p className="px-3 py-4 text-center text-xs text-slate-500">No matches</p>
            ) : (
              grouped.map(([date, items]) => (
                <div key={date} className="mb-1">
                  <p className="sticky top-0 bg-white px-3 py-1 text-[10px] font-medium uppercase tracking-wide text-slate-400">
                    {date}
                    {!items[0]!.onChart ? (
                      <span className="ml-1 normal-case text-slate-400">· off chart</span>
                    ) : null}
                  </p>
                  <ul className="m-0 list-none p-0">
                    {items.map((r, idx) => (
                      <li key={`${date}-${r.txnCode}-${r.reportingName}-${idx}`}>
                        <button
                          type="button"
                          className="flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left hover:bg-amber-50/80"
                          onClick={() => {
                            onSelectResult(r);
                            setOpen(false);
                          }}
                        >
                          <span className="min-w-0">
                            <span className="block truncate text-xs font-medium text-slate-800">
                              {r.reportingName}
                            </span>
                            <span className="block truncate text-[10px] text-slate-500">
                              {r.typeLabel}
                            </span>
                          </span>
                          <span className="shrink-0 text-[10px] text-slate-500">{r.metricLabel}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              ))
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
